import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SeoHead from "../SiteSeo";
import SiteLayout from "../SiteLayout";
import { CalendlyLink, SmartLink } from "../SiteHeader";
import { LeadCapture } from "./CalculatorPage";
import { toolBySlug } from "./tools";
import { ALL_ITEMS, BUDGET_CATEGORIES } from "./budgetBoostItems";
import { HotTakeBand } from "../HotTakeRail";

const API = process.env.REACT_APP_BACKEND_URL;
const money = (v) => `$${Math.round(Number(v) || 0).toLocaleString("en-CA")}`;
const HERO = "/brand/budget-boost-hero.jpg";

function parseState(search) {
  const q = new URLSearchParams(search);
  const picks = (q.get("picks") || "").split(",").filter((id) => ALL_ITEMS[id]);
  const custom = (q.get("custom") || "").split("|").filter(Boolean).map((s) => { const [name, amt] = s.split(":"); return { name: decodeURIComponent(name || ""), amount: Number(amt) || 0 }; }).filter((c) => c.amount > 0);
  return { picks: new Set(picks), custom };
}

function Chip({ item, on, toggle }) {
  return (
    <button type="button" className={`bb-chip${on ? " on" : ""}`} aria-pressed={on} onClick={() => toggle(item.id)} data-testid={`bb-item-${item.id}`}>
      <span className="bb-emoji" aria-hidden="true">{item.emoji}</span>
      <span className="bb-name">{item.name}</span>
      <span className="bb-amt">${item.amount}<small>/mo</small></span>
      {on && item.quip && <span className="bb-quip">{item.quip}</span>}
    </button>
  );
}

function CustomRow({ custom, setCustom }) {
  const [name, setName] = useState("");
  const [amt, setAmt] = useState("");
  const add = () => { if (!name.trim() || !(Number(amt) > 0)) return; setCustom([...custom, { name: name.trim().slice(0, 40), amount: Math.round(Number(amt)) }]); setName(""); setAmt(""); };
  return (
    <div className="bb-custom" data-testid="bb-custom">
      <h2>What else are you splurging on? 🤔</h2>
      <div className="bb-custom-row">
        <input placeholder="Name it (e.g. Lego habit)" value={name} onChange={(e) => setName(e.target.value)} aria-label="Custom expense name" data-testid="bb-custom-name" />
        <div className="calc-input"><em>$</em><input type="number" placeholder="per month" value={amt} onChange={(e) => setAmt(e.target.value)} aria-label="Custom expense amount" data-testid="bb-custom-amount" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} /></div>
        <button type="button" className="btn outline" onClick={add} data-testid="bb-custom-add">Add it ➕</button>
      </div>
      {custom.length > 0 && <div className="bb-custom-list">{custom.map((c, i) => <button type="button" key={`${c.name}-${i}`} className="bb-chip on" onClick={() => setCustom(custom.filter((_, j) => j !== i))} data-testid={`bb-custom-item-${i}`}><span className="bb-emoji" aria-hidden="true">🫣</span><span className="bb-name">{c.name}</span><span className="bb-amt">${c.amount}<small>/mo</small></span><span className="bb-quip">Tap to remove</span></button>)}</div>}
    </div>
  );
}

function Reveal({ total, results, picks, custom, rate, shareUrl, tool }) {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    const text = `I just found ${money(total)}/mo hiding in my spending — that's ${money(results?.buying_power)} of extra house. Find yours:`;
    if (navigator.share) { try { await navigator.share({ title: "Budget Boost", text, url: shareUrl }); return; } catch { /* cancelled */ } }
    try { await navigator.clipboard.writeText(`${text} ${shareUrl}`); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch { window.prompt("Copy this link", shareUrl); }
  };
  const selected = [...picks].map((id) => ALL_ITEMS[id]).concat(custom.map((c) => ({ id: c.name, name: c.name, amount: c.amount, emoji: "🫣" })));
  return (
    <aside className="calc-results bb-reveal" aria-live="polite" data-testid="bb-reveal">
      <div className="eyebrow" style={{ color: "var(--pink2)" }}>The big reveal 📈</div>
      <h3 className="bb-plot">{total > 0 ? "Plot twist: you’re richer 💰" : "Tap a few splurges to start ↖"}</h3>
      <div className="bb-stat"><span>Hiding in your spending</span><strong data-testid="bb-monthly">{money(total)}<small>/mo</small></strong></div>
      <div className="bb-stat big"><span>Your hidden home potential 🔥</span><strong data-testid="bb-buying-power">{money(results?.buying_power)}</strong><small>more house your monthly ‘whatever’ money supports{rate ? ` at ${rate}% over 25 years` : ""}</small></div>
      <dl className="calc-summary">
        <div><dt>Down payment in 3 years</dt><dd data-testid="bb-down-payment">{money(results?.down_payment)}</dd></div>
        <div><dt>Back in your pocket every year</dt><dd data-testid="bb-annual">{money(results?.annual_saved)}</dd></div>
        <div><dt>Over 10 years</dt><dd>{money(results?.ten_year_total)}</dd></div>
      </dl>
      {selected.length > 0 && <div className="bb-selected" data-testid="bb-selected"><h4>Your picks</h4><ul>{selected.map((s) => <li key={s.id}>{s.emoji} {s.name} <b>${s.amount}</b></li>)}</ul></div>}
      <div className="bb-actions">
        <CalendlyLink className="btn" testId="bb-lets-talk">Make moves → Let’s talk 🏠</CalendlyLink>
        <button type="button" className="btn light" onClick={share} disabled={total <= 0} data-testid="bb-share">{copied ? "Link copied ✓" : "Drop this in the group chat 😈"}</button>
      </div>
      {total > 0 && <LeadCapture tool={tool} inputs={{ monthly_saved: total, picks: selected.map((s) => s.name) }} results={results || {}} />}
    </aside>
  );
}

export default function BudgetBoost() {
  const tool = toolBySlug("budget-boost");
  const location = useLocation();
  const navigate = useNavigate();
  const init = useMemo(() => parseState(location.search), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [picks, setPicks] = useState(init.picks);
  const [custom, setCustom] = useState(init.custom);
  const [rate, setRate] = useState(null);
  const [results, setResults] = useState(null);
  const timer = useRef(null);
  const total = [...picks].reduce((s, id) => s + ALL_ITEMS[id].amount, 0) + custom.reduce((s, c) => s + c.amount, 0);
  const toggle = (id) => setPicks((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  useEffect(() => { fetch(`${API}/api/public/calc/defaults`).then((r) => r.json()).then((d) => setRate(d.fixed_5yr || 4.79)).catch(() => setRate(4.79)); }, []);

  useEffect(() => {
    const q = new URLSearchParams();
    if (picks.size) q.set("picks", [...picks].join(","));
    if (custom.length) q.set("custom", custom.map((c) => `${encodeURIComponent(c.name)}:${c.amount}`).join("|"));
    navigate({ search: q.toString() ? `?${q}` : "" }, { replace: true });
  }, [picks, custom, navigate]);

  useEffect(() => {
    if (rate == null) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/api/public/calc/budget-boost`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ monthly_saved: total, rate }) });
        if (r.ok) setResults(await r.json());
      } catch { /* keep last */ }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [total, rate]);

  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/calculators/budget-boost/${location.search}` : "";
  const ld = { "@context": "https://schema.org", "@type": "WebApplication", name: "Budget Boost", applicationCategory: "FinanceApplication", operatingSystem: "Web", url: "https://www.hellomortgage.ca/calculators/budget-boost/", offers: { "@type": "Offer", price: "0", priceCurrency: "CAD" }, provider: { "@type": "Organization", name: "Hello Mortgage Team" } };

  return (
    <SiteLayout>
      <SeoHead title="Budget Boost — Find the House Hiding in Your Spending | Hello Mortgage Team" description={tool.blurb} path="/calculators/budget-boost/" ogImage={`https://www.hellomortgage.ca${HERO}`} />
      <Helmet><script type="application/ld+json">{JSON.stringify(ld)}</script></Helmet>
      <section className="calc-hero bb-hero" data-testid="calculator-budget-boost">
        <div className="wrap bb-hero-grid">
          <div>
            <SmartLink className="textlink" href="/calculators/">← All calculators</SmartLink>
            <div className="eyebrow" style={{ marginTop: 26 }}>Budget Boost 🏠</div>
            <h1>{tool.tagline}</h1>
            <p>{tool.blurb}</p>
            <p className="micro">No judgment. Okay, a little judgment. Tap everything you could live without.</p>
          </div>
          <img src={HERO} alt="Budget Boost — a piggy bank shaped like a house, coins dropping in. Mind on my money, money on my mind." className="bb-hero-art" data-testid="bb-hero-art" />
        </div>
      </section>
      <section className="calc-body">
        <div className="wrap calc-grid bb-grid">
          <div className="bb-board" data-testid="bb-board">
            {BUDGET_CATEGORIES.map((c) => (
              <div className="bb-cat" key={c.key} data-testid={`bb-cat-${c.key}`}>
                <h2>{c.name}</h2>
                <div className="bb-chips">{c.items.map((it) => <Chip key={it.id} item={it} on={picks.has(it.id)} toggle={toggle} />)}</div>
              </div>
            ))}
            <CustomRow custom={custom} setCustom={setCustom} />
          </div>
          <Reveal total={total} results={results} picks={picks} custom={custom} rate={rate} shareUrl={shareUrl} tool={tool} />
        </div>
        <div className="wrap calc-fine"><p><strong>The fine print ✨</strong> “Hidden home potential” is the extra mortgage your monthly amount supports — principal and interest only, at {rate || "today’s"}% over 25 years with Canadian semi-annual compounding. Real life adds property tax, insurance, heat and the stress test. The 3-year down payment assumes 3% growth in a high-interest savings account or FHSA. Not a pre-approval (duh) — talk to us for your true buying power.</p></div>
      </section>
      <section className="bb-share-band">
        <div className="wrap">
          <div className="eyebrow" style={{ color: "var(--pink2)" }}>Spread the wealth 💸</div>
          <h2 className="display">Drop this in the group chat.</h2>
          <p>Send your friends the link — their picks, their reveal, their mild personal crisis. Everyone wins.</p>
          <SmartLink className="btn light" href="/calculators/budget-boost/" data-testid="bb-fresh-link">Start a fresh one for a friend →</SmartLink>
        </div>
      </section>
      <HotTakeBand page="budget-boost" tag={<>Matt’s Hot Take · Where the house is <em>actually</em> hiding</>} testId="budget-boost-hot-take" />
    </SiteLayout>
  );
}
